import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

// Firebase
import { db } from '../../../firebase/config';
import { 
    collection, query, where, getDocs, addDoc, doc, updateDoc, setDoc,
    limit, collectionGroup, serverTimestamp 
} from 'firebase/firestore';

// IMPORTAMOS LA UTILIDAD DE BORRADO PROFUNDO
import { deleteRemesaFull } from '../../../firebase/firebaseUtils';

// Componentes UI
import ModalConfirmacion from '../../ui/ModalConfirmacion'; 

// Estrategias
import { SancorStrategy } from './SancorStrategy';
import { LaSegundaStrategy } from './LaSegundaStrategy';
import { MercantilStrategy } from './MercantilStrategy';
import { GenericStrategy } from './GenericStrategy';
import { AtmStrategy } from './AtmStrategy';
import { DignaStrategy } from './DignaStrategy';

const strategies = {
    'SANCOR': SancorStrategy,
    'LA SEGUNDA': LaSegundaStrategy,
    'MERCANTIL': MercantilStrategy,
    'GENERICO': GenericStrategy,
    'ATM': AtmStrategy,
    'DIGNA': DignaStrategy,
};

// --- HELPERS DE BÚSQUEDA ---
const findValue = (row, possibleKeys) => {
    const rowKeys = Object.keys(row);
    const normalizedKeys = rowKeys.reduce((acc, key) => {
        acc[key.trim().toLowerCase()] = key;
        return acc;
    }, {});
    for (const key of possibleKeys) {
        const searchKey = key.toLowerCase();
        if (normalizedKeys[searchKey]) return row[normalizedKeys[searchKey]];
    }
    return undefined;
};

// Helper para dividir un array en grupos de tamaño N
const chunkArray = (array, size) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
        chunks.push(array.slice(i, i + size));
    }
    return chunks;
};

/**
 * Motor de Sanitización Estricto: Previene el guardado de objetos Date nativos
 * en campos de texto (ej. moneda) que provocan pantallas en blanco en el renderizado de React.
 */
const sanitizeString = (val, fallback = '') => {
    if (val === undefined || val === null) return fallback;
    if (val instanceof Date) {
        return val.toLocaleDateString('es-AR');
    }
    if (typeof val === 'object') {
        return fallback;
    }
    return String(val).trim();
};

const sanitizeValue = (val) => {
    if (val === undefined || val === null) return null;
    if (val instanceof Date) {
        return val.toISOString().split('T')[0];
    }
    if (typeof val === 'object') {
        return null; // Quita objetos de control de memoria
    }
    return val;
};

const ImportarDeudaPage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('importar'); 
    const [file, setFile] = useState(null);
    const [company, setCompany] = useState('SANCOR'); 
    const [processing, setProcessing] = useState(false);
    const [logs, setLogs] = useState([]); 
    const [stats, setStats] = useState({ success: 0, updated: 0, errors: 0 });
    const fileInputRef = useRef(null);

    // Estados para la Vista Previa Inteligente
    const [previewRows, setPreviewRows] = useState([]);
    const [showPreview, setShowPreview] = useState(false);

    // Caché local segmentada (Solo guardamos las pólizas involucradas en el Excel actual)
    const [polizasCache, setPolizasCache] = useState([]);
    const [loadingCaches, setLoadingCaches] = useState(false);

    const [remesas, setRemesas] = useState([]);
    const [remesaToDelete, setRemesaToDelete] = useState(null);
    const [loadingRemesas, setLoadingRemesas] = useState(false);

    // --- Paginación del Historial de Remesas ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    // --- 1. SINK OPTIMIZADO: Busca ÚNICAMENTE los números de póliza que están en el Excel ---
    const fetchPolizasCacheForNumbers = async (polizasANecesitar) => {
        if (polizasANecesitar.length === 0) return [];
        setLoadingCaches(true);
        addLog(`[SISTEMA] Buscando ${polizasANecesitar.length} pólizas únicas en la base de datos...`, "warning");
        
        try {
            // Firestore limita el operador 'in' a un máximo de 30 elementos por consulta.
            // Dividimos nuestra lista de pólizas en grupos de 30.
            const chunks = chunkArray(polizasANecesitar, 30);
            
            // Ejecutamos las consultas de forma paralela en la red para máxima velocidad
            const promises = chunks.map(async (chunk) => {
                const qPolizas = query(
                    collectionGroup(db, 'polizas'), 
                    where('nPoliza', 'in', chunk)
                );
                const snap = await getDocs(qPolizas);
                return snap.docs.map(d => ({
                    id: d.id,
                    path: d.ref.path,
                    clientId: d.ref.parent.parent.id,
                    ...d.data()
                }));
            });

            const results = await Promise.all(promises);
            const mapped = results.flat(); // Fusionamos todos los pedazos en un único array plano
            
            setPolizasCache(mapped);
            addLog(`[SISTEMA] Sincronización exitosa. Se encontraron ${mapped.length} de las ${polizasANecesitar.length} pólizas requeridas.`, "success");
            return mapped;
        } catch (error) {
            console.error("Error al indexar de forma segmentada:", error);
            addLog("Error crítico de sincronización segmentada de pólizas.", "error");
            return [];
        } finally {
            setLoadingCaches(false);
        }
    };

    // --- Obtención de Remesas de los últimos 3 meses ---
    const fetchRemesas = async () => {
        setLoadingRemesas(true);
        try {
            const snap = await getDocs(collection(db, 'public/data/remesas_deuda'));
            
            const limiteMeses = new Date();
            limiteMeses.setMonth(limiteMeses.getMonth() - 3);
            const limitTime = limiteMeses.getTime();

            const listadoFiltrado = snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(r => {
                    const remesaDate = r.fecha ? new Date(r.fecha).getTime() : 0;
                    return remesaDate >= limitTime;
                });

            listadoFiltrado.sort((a, b) => {
                const dateA = a.fecha ? new Date(a.fecha).getTime() : 0;
                const dateB = b.fecha ? new Date(b.fecha).getTime() : 0;
                return dateB - dateA;
            });

            setRemesas(listadoFiltrado);
            setCurrentPage(1); 
        } catch (error) { 
            console.error("Error remesas:", error); 
            addLog("Error de red al intentar sincronizar el historial de remesas.", "error");
        } finally { 
            setLoadingRemesas(false); 
        }
    };

    useEffect(() => { 
        if (activeTab === 'remesas') fetchRemesas(); 
    }, [activeTab]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        setProcessing(false); 
        setLogs([]); 
        setStats({ success: 0, updated: 0, errors: 0 }); 
        setPreviewRows([]);
        setShowPreview(false);
        setFile(selectedFile);
        setPolizasCache([]); // Reseteamos la caché al cambiar el archivo
    };

    const addLog = (msg, type = 'info') => {
        setLogs(prev => [...prev, { msg, type, time: new Date().toLocaleTimeString() }]);
    };

    const handleCancelPreview = () => {
        setFile(null);
        setPreviewRows([]);
        setShowPreview(false);
        setLogs([]);
        setStats({ success: 0, updated: 0, errors: 0 });
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // --- 2. INDEXACIÓN SÍNCRONA EN MEMORIA ---
    const parseExcelToPreview = async () => {
        if (!file) return;
        setProcessing(true);
        setLogs([]);
        setStats({ success: 0, updated: 0, errors: 0 });

        const activeStrategy = strategies[company];

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = e.target.result;
                const workbook = XLSX.read(data, { type: 'binary', cellDates: true });
                const json = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]]);

                if (json.length === 0) {
                    addLog("Archivo seleccionado vacío.", "error");
                    setProcessing(false);
                    return;
                }

                // 1. Extraemos de forma limpia y única todos los números de póliza que vienen en el Excel
                const polizasEnExcel = Array.from(new Set(
                    json.map(row => {
                        let nPolizaRaw = findValue(row, ['nPoliza', 'Poliza', 'POLIZA', 'Nro Poliza', 'Póliza'])?.toString().trim() || '';
                        if (nPolizaRaw.endsWith('.0')) nPolizaRaw = nPolizaRaw.replace('.0', '');
                        return activeStrategy.cleanSearchValue(nPolizaRaw);
                    }).filter(Boolean)
                ));

                // 2. Buscamos SÓLO esas pólizas en Firebase (Cero descargas innecesarias)
                const currentCache = await fetchPolizasCacheForNumbers(polizasEnExcel);

                if (currentCache.length === 0) {
                    addLog("Advertencia: No se encontró correspondencia de ninguna póliza en la base de datos.", "warning");
                }

                addLog(`Indexando ${json.length} registros en memoria local...`, 'info');

                // Mapear pólizas indexadas para matching O(1) instantáneo
                const polizasMap = {};
                currentCache.forEach(p => {
                    if (p.nPoliza) {
                        polizasMap[String(p.nPoliza).trim()] = p;
                    }
                });

                let listos = 0;
                let erroneos = 0;
                const rowsProcessed = [];

                // Recorremos las filas para construir la previsualización
                for (const row of json) {
                    let nPolizaRaw = findValue(row, ['nPoliza', 'Poliza', 'POLIZA', 'Nro Poliza', 'Póliza'])?.toString().trim() || '';
                    if (!nPolizaRaw) continue;

                    if (nPolizaRaw.endsWith('.0')) nPolizaRaw = nPolizaRaw.replace('.0', '');
                    const nPolizaBusqueda = activeStrategy.cleanSearchValue(nPolizaRaw);

                    const polizaEncontrada = polizasMap[nPolizaBusqueda];
                    const hasMatch = !!polizaEncontrada;

                    const rawImporte = findValue(row, ['Importe', 'Monto', 'Total', 'Valor']);
                    const rawMoneda = findValue(row, ['Moneda', 'Divisa']) || '$';
                    const monedaSanitizada = sanitizeString(rawMoneda, '$');

                    if (hasMatch) listos++;
                    else erroneos++;

                    rowsProcessed.push({
                        nPoliza: nPolizaBusqueda,
                        importe: rawImporte,
                        moneda: monedaSanitizada,
                        polizaPadre: polizaEncontrada || null,
                        isCorrect: hasMatch,
                        rowRaw: row,
                        statusText: hasMatch ? 'Listo para importar' : 'Póliza no encontrada'
                    });
                }

                setPreviewRows(rowsProcessed);
                setStats({ success: listos, updated: 0, errors: erroneos });
                setShowPreview(true);
                addLog(`Indexación terminada. ${listos} listos para importar. ${erroneos} fallidos.`, 'info');

            } catch (error) {
                addLog(`Error de lectura: ${error.message}`, 'error');
            } finally {
                setProcessing(false);
            }
        };
        reader.readAsBinaryString(file);
    };

    // --- 3. CONFIRMACIÓN Y PERSISTENCIA FINAL ---
    const handleConfirmImport = async () => {
        if (previewRows.length === 0 || processing) return;
        setProcessing(true);
        addLog("Iniciando guardado de deudas en base de datos...", "info");

        try {
            const activeStrategy = strategies[company];
            const batchId = new Date().getTime().toString();

            // PRE-GENERAMOS EL ID DE LA REMESA PARA USARLO EN EL PAYLOAD
            const remesaDocRef = doc(collection(db, 'public/data/remesas_deuda'));
            const remesaDocId = remesaDocRef.id;

            const cobranzaCache = {};
            const creadosPaths = [];
            let s = 0, u = 0, err = 0;

            for (const item of previewRows) {
                if (!item.isCorrect) continue;

                try {
                    const polizaDoc = item.polizaPadre;
                    const pData = polizaDoc;
                    const clientId = polizaDoc.clientId;
                    const fullCobranzaPath = `public/data/clientes/${clientId}/polizas/${polizaDoc.id}/cobranza`;

                    if (!cobranzaCache[polizaDoc.id]) {
                        const snapCob = await getDocs(collection(db, fullCobranzaPath));
                        cobranzaCache[polizaDoc.id] = snapCob.docs.map(d => ({ id: d.id, ...d.data() }));
                    }

                    const existingRecord = activeStrategy.findExistingRecord 
                        ? activeStrategy.findExistingRecord(item.rowRaw, cobranzaCache[polizaDoc.id])
                        : null;

                    const result = activeStrategy.processRow(item.rowRaw, existingRecord);
                    if (result.action === 'SKIP') { err++; continue; }

                    const payload = {
                        ...result.data,
                        clienteId: clientId,
                        clienteNombre: sanitizeString(pData.clienteNombre, 'S/D'),
                        clienteApellido: sanitizeString(pData.clienteApellido, 'S/D'),
                        clienteCuit: sanitizeString(pData.clienteCuit, ''),
                        estado: 'Sin Asignar',
                        clienteDni: sanitizeString(pData.clienteDni, ''),
                        polizaId: polizaDoc.id,
                        nPoliza: sanitizeString(pData.nPoliza, item.nPoliza),
                        compania: sanitizeString(pData.compania, company),
                        companiaId: sanitizeString(pData.companiaId, ''),
                        riesgo: sanitizeString(pData.riesgo, ''),
                        riesgoId: sanitizeString(pData.riesgoId, ''),
                        subriesgo: sanitizeString(pData.subriesgo, ''),
                        canal: sanitizeString(pData.canal, ''),
                        canalNombre: sanitizeString(pData.canalNombre, ''),
                        canalNombreId: sanitizeString(pData.canalNombreId, ''),
                        subcanalNombre: sanitizeString(pData.subcanalNombre, ''),
                        subcanalNombreId: sanitizeString(pData.subcanalNombreId, ''),
                        batchId: batchId,
                        remesaDocId: remesaDocId,
                        fechaCreacion: serverTimestamp(),
                        fechaCarga: new Date().toISOString().split('T')[0],
                        ultimaModificacion: serverTimestamp()
                    };

                    payload.moneda = sanitizeString(item.moneda, '$');

                    delete payload.id;
                    delete payload.vencimiento;
                    delete payload.cuit;
                    delete payload.dni;

                    Object.keys(payload).forEach(key => {
                        payload[key] = sanitizeValue(payload[key]);
                    });

                    const cobranzaRef = collection(db, fullCobranzaPath);

                    if (existingRecord) {
                        const docRef = doc(cobranzaRef, existingRecord.id);
                        await updateDoc(docRef, payload);
                        addLog(`[ACTUALIZADO] Póliza #${item.nPoliza}`, 'success'); 
                        u++;
                        creadosPaths.push(docRef.path);
                    } else {
                        payload.fechaCarga = new Date().toISOString().split('T')[0];
                        const newDocRef = await addDoc(cobranzaRef, payload);
                        addLog(`[NUEVO] Póliza #${item.nPoliza}`, 'success'); 
                        s++;
                        creadosPaths.push(newDocRef.path);
                    }

                    if (!pData.tieneDeuda) {
                        await updateDoc(doc(db, `public/data/clientes/${clientId}/polizas`, polizaDoc.id), { tieneDeuda: true });
                    }

                } catch (rowError) {
                    addLog(`Error en fila ${item.nPoliza}: ${rowError.message}`, 'error');
                    err++;
                }
            }

            // GUARDAMOS EL REGISTRO DE LA REMESA MAESTRA INCLUYENDO EL ARRAY DE PATHS EXACTOS
            await setDoc(remesaDocRef, {
                fecha: new Date().toISOString(), 
                compania: company, 
                archivo: file.name, 
                registros: s + u, 
                batchId: batchId,
                documentosCreados: creadosPaths
            });

            setStats({ success: s, updated: u, errors: err });
            addLog('--- FIN DEL PROCESO ---', 'info');
            setFile(null);
            setPreviewRows([]);
            setShowPreview(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
            setPolizasCache([]); // Reseteamos la caché al finalizar

        } catch (error) {
            console.error("Error crítico de importación masiva:", error);
            addLog(`Error crítico de guardado: ${error.message}`, 'error');
        } finally {
            setProcessing(false);
        }
    };

    const handleDeleteRemesa = async () => {
        if (!remesaToDelete) return;
        setProcessing(true);
        try {
            await deleteRemesaFull(remesaToDelete.batchId, remesaToDelete.id);
            setRemesaToDelete(null); 
            fetchRemesas();
        } catch (error) { 
            console.error(error); 
        } finally { 
            setProcessing(false); 
        }
    };

    const totalPages = Math.ceil(remesas.length / itemsPerPage);
    const paginatedRemesas = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return remesas.slice(start, start + itemsPerPage);
    }, [remesas, currentPage]);

    return (
        <div className="min-h-screen bg-slate-50 p-8 md:p-12 font-sans text-slate-800 text-left transition-all duration-300">
            <div className="max-w-[95%] mx-auto">
                <div className="flex justify-between items-center mb-8 text-left select-none">
                    <div>
                        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight text-left uppercase">Importador de Deuda</h1>
                        <p className="text-slate-500 mt-1.5 text-sm text-left font-medium">Carga masiva profesional con previsualización síncrona y blindaje de tipos de datos.</p>
                    </div>
                    <button onClick={() => navigate('/cpanel')} className="px-5 py-2.5 text-slate-700 bg-white border border-slate-200 rounded-xl font-bold hover:text-blue-600 shadow-sm transition-all uppercase text-[10px] tracking-widest active:scale-95 transform">Volver al Cpanel</button>
                </div>

                <div className="flex space-x-1 bg-slate-200 p-1 rounded-xl w-fit mb-6 text-left select-none">
                    <button onClick={() => setActiveTab('importar')} className={`px-6 py-2 rounded-lg text-xs font-black uppercase transition-all ${activeTab === 'importar' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>Importar Archivo</button>
                    <button onClick={() => setActiveTab('remesas')} className={`px-6 py-2 rounded-lg text-xs font-black uppercase transition-all ${activeTab === 'remesas' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>Historial Remesas</button>
                </div>

                {activeTab === 'importar' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-fade-in text-left">
                        <div className="lg:col-span-1 space-y-6 text-left">
                            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 text-left select-none">
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2 text-left">Compañía / Estrategia</label>
                                <select value={company} onChange={(e) => setCompany(e.target.value)} className="w-full border border-blue-200 bg-blue-50 text-blue-900 font-bold p-3 rounded-xl outline-none" disabled={showPreview}>
                                    {Object.keys(strategies).map(k => <option key={k} value={k}>{k}</option>)}
                                </select>
                            </div>

                            {!showPreview ? (
                                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 text-center select-none">
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2 text-left">Archivo Excel</label>
                                    <div className="relative border-2 border-dashed border-slate-200 rounded-2xl p-6 hover:bg-slate-50 transition cursor-pointer flex flex-col items-center justify-center">
                                        <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".xlsx, .xls" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" disabled={processing} />
                                        <svg className="w-8 h-8 text-slate-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                                        <p className="text-sm text-slate-500 font-bold tracking-tight">{file ? file.name : "Clic o arrastrar archivo"}</p>
                                    </div>
                                    <button onClick={parseExcelToPreview} disabled={!file || processing || loadingCaches} className={`w-full py-4 mt-6 rounded-xl font-black text-white shadow-lg flex items-center justify-center gap-2 uppercase text-xs tracking-widest transition-all ${!file || processing || loadingCaches ? 'bg-slate-300 shadow-none cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 shadow-blue-100 active:scale-95 transform'}`}>
                                        {loadingCaches ? 'Buscando Pólizas...' : processing ? 'Indexando...' : 'Previsualizar Lote'}
                                    </button>
                                </div>
                            ) : (
                                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 text-left space-y-4">
                                    <h3 className="text-sm font-black text-slate-700 uppercase tracking-wider border-b pb-2 select-none">Confirmar Remesa</h3>
                                    <div className="flex gap-2">
                                        <button onClick={handleCancelPreview} className="flex-1 py-3.5 border border-slate-300 text-slate-600 font-bold uppercase text-xs tracking-wider rounded-xl hover:bg-slate-50 transition active:scale-95 transform select-none">
                                            Cancelar
                                        </button>
                                        <button onClick={handleConfirmImport} disabled={stats.success === 0 || processing} className="flex-2 py-3.5 px-6 bg-blue-600 text-white font-black uppercase text-xs tracking-wider rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-100 active:scale-95 transform select-none disabled:opacity-50 disabled:cursor-not-allowed">
                                            {processing ? 'Guardando...' : 'Confirmar Lote'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-3 gap-2 text-center font-bold select-none">
                                <div className="bg-green-50 p-3 rounded-lg border border-green-100 text-green-600"><div>{stats.success}</div><div className="text-[9px] uppercase text-center font-black">Sin Errores</div></div>
                                <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 text-blue-600"><div>{stats.updated}</div><div className="text-[9px] uppercase text-center font-black">Actualiz.</div></div>
                                <div className="bg-red-50 p-3 rounded-lg border border-red-100 text-red-600"><div>{stats.errors}</div><div className="text-[9px] uppercase text-center font-black">Con Errores</div></div>
                            </div>
                        </div>

                        {/* SECCIÓN DERECHA: CONSOLA DE LOGS O TABLA DE PREVISUALIZACIÓN */}
                        <div className="lg:col-span-2 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden flex flex-col h-[600px] text-left relative">
                            {!showPreview ? (
                                <div className="p-6 flex flex-col h-full bg-slate-900 border border-slate-800 font-mono text-xs relative overflow-hidden text-left">
                                    <p className="text-slate-500 font-bold uppercase tracking-widest mb-4 border-b border-slate-800 pb-2 text-[10px] select-none">Consola de Control del Lote</p>
                                    <div className="flex-grow overflow-y-auto space-y-1.5 pr-2 custom-scrollbar text-left font-semibold">
                                        {loadingCaches && (
                                            <div className="text-amber-400 animate-pulse text-left">[SISTEMA] Sincronizando diccionario de pólizas de la base de datos...</div>
                                        )}
                                        {logs.length === 0 && !loadingCaches && (
                                            <div className="text-slate-500 italic">Consola vacía. Seleccione un archivo Excel para iniciar la sincronización y previsualización.</div>
                                        )}
                                        {logs.map((l, i) => (
                                            <div key={i} className={`flex gap-3 text-left ${l.type === 'error' ? 'text-rose-400 font-bold' : l.type === 'warning' ? 'text-amber-400' : l.type === 'success' ? 'text-emerald-400 font-bold' : 'text-slate-300'}`}>
                                                <span className="opacity-40 tabular-nums">[{l.time}]</span><span>{l.msg}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col h-full animate-fade-in text-left">
                                    <div className="p-4 bg-slate-50 border-b select-none flex justify-between items-center shrink-0">
                                        <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest">Vista previa de deudas cargadas</h4>
                                        <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-100 uppercase">Listo para revisión</span>
                                    </div>
                                    <div className="flex-grow overflow-auto scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
                                        <table className="w-full text-left text-xs border-collapse">
                                            <thead className="bg-slate-50/50 border-b text-slate-500 uppercase font-black text-[9px] tracking-wider select-none sticky top-0 bg-white z-10">
                                                <tr>
                                                    <th className="px-4 py-3">Póliza N°</th>
                                                    <th className="px-4 py-3">Asegurado / Cía</th>
                                                    <th className="px-4 py-3 text-right">Importe / Moneda</th>
                                                    <th className="px-4 py-3 text-center">Estado</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {previewRows.map((row, idx) => (
                                                    <tr key={idx} className={`hover:bg-slate-50/50 ${!row.isCorrect ? 'bg-rose-50/30' : ''}`}>
                                                        <td className="px-4 py-3 font-bold text-slate-800">#{row.nPoliza}</td>
                                                        <td className="px-4 py-3">
                                                            {row.polizaPadre ? (
                                                                <div className="flex flex-col text-left">
                                                                    <span className="font-bold text-slate-700">{row.polizaPadre.clienteNombre}</span>
                                                                    <span className="text-[10px] text-slate-400 font-bold uppercase">{row.polizaPadre.compania}</span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-rose-500 font-bold italic select-none">No aplica</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 text-right font-mono">
                                                            <div className="font-black text-slate-800">{row.moneda} {row.importe}</div>
                                                        </td>
                                                        <td className="px-4 py-3 text-center select-none">
                                                            <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase border tracking-tight ${
                                                                row.isCorrect 
                                                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
                                                                    : 'bg-rose-50 text-rose-700 border-rose-100 font-black'
                                                            }`}>
                                                                {row.statusText}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'remesas' && (
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden animate-fade-in flex flex-col justify-between min-h-[500px] text-left">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm border-collapse">
                                <thead className="bg-gray-50 text-slate-500 uppercase font-bold text-[10px] tracking-widest border-b select-none">
                                    <tr>
                                        <th className="px-6 py-4">Fecha (Últimos 3 Meses)</th>
                                        <th className="px-6 py-4">Cía</th>
                                        <th className="px-6 py-4">Archivo</th>
                                        <th className="px-6 py-4 text-center">Registros</th>
                                        <th className="px-6 py-4 text-center">Acción</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {loadingRemesas ? (
                                        <tr><td colSpan="5" className="p-16 text-center text-gray-400 animate-pulse font-bold text-xs uppercase tracking-widest">Cargando historial de remesas...</td></tr>
                                    ) : remesas.length === 0 ? (
                                        <tr><td colSpan="5" className="p-16 text-center text-gray-400 italic">No se encontraron remesas registradas in los últimos 3 meses.</td></tr>
                                    ) : (
                                        paginatedRemesas.map(r => (
                                            <tr key={r.id} className="hover:bg-blue-50/50 transition-colors">
                                                <td className="px-6 py-4 font-mono text-gray-600">{new Date(r.fecha).toLocaleString('es-AR')}</td>
                                                <td className="px-6 py-4 font-bold text-gray-800">{r.compania}</td>
                                                <td className="px-6 py-4 text-gray-500 italic select-all">{r.archivo}</td>
                                                <td className="px-6 py-4 text-center font-bold text-blue-600">{r.registros}</td>
                                                <td className="px-6 py-4 text-center">
                                                    <button onClick={() => setRemesaToDelete(r)} className="text-rose-500 hover:text-white border border-rose-100 hover:bg-rose-500 px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all select-none">
                                                        Eliminar
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Panel de Paginación Interactivo con Estilos Nativos Tailwind */}
                        {totalPages > 1 && !loadingRemesas && (
                            <div className="px-6 py-4 bg-slate-50 border-t flex items-center justify-between select-none shrink-0">
                                <div className="text-xs text-slate-500 font-semibold">
                                    Mostrando <span className="font-bold text-slate-700">{((currentPage - 1) * itemsPerPage) + 1}</span> a{' '}
                                    <span className="font-bold text-slate-700">{Math.min(currentPage * itemsPerPage, remesas.length)}</span> de{' '}
                                    <span className="font-bold text-slate-700">{remesas.length}</span> remesas
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                        className={`px-3 py-1.5 rounded-lg border text-xs font-black uppercase tracking-wider transition-all ${
                                            currentPage === 1
                                                ? 'bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed'
                                                : 'bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-800 border-slate-200 active:scale-95 transform'
                                        }`}
                                    >
                                        Anterior
                                    </button>
                                    
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                        <button
                                            key={page}
                                            type="button"
                                            onClick={() => setCurrentPage(page)}
                                            className={`w-8 h-8 rounded-lg border text-xs font-black transition-all ${
                                                currentPage === page
                                                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm shadow-blue-100'
                                                    : 'bg-white text-slate-500 hover:bg-slate-100 border-slate-200'
                                            }`}
                                        >
                                            {page}
                                        </button>
                                    ))}

                                    <button
                                        type="button"
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                        className={`px-3 py-1.5 rounded-lg border text-xs font-black uppercase tracking-wider transition-all ${
                                            currentPage === totalPages
                                                ? 'bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed'
                                                : 'bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-800 border-slate-200 active:scale-95 transform'
                                        }`}
                                    >
                                        Siguiente
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <ModalConfirmacion show={!!remesaToDelete} onClose={() => setRemesaToDelete(null)} onConfirm={handleDeleteRemesa} title="Eliminar Lote" message="¿Seguro que desea eliminar esta remesa y todos sus registros?" />
        </div>
    );
};

export default ImportarDeudaPage;